import './model-filters.scss';

class ModelFilters {
	constructor( el ) {
		this.el          = el;
		this.barEl       = el.querySelector( '.js-filtersBar' );
		this.panelEl     = el.querySelector( '.js-filtersPanel' );
		this.toggleBtn   = el.querySelector( '.js-filtersToggle' );
		this.badgeEl     = el.querySelector( '.js-filtersBadge' );
		this.resetBtn    = el.querySelector( '.js-resetFilters' );
		this.moreToggle  = el.querySelector( '.js-moreToggle' );
		this.moreText    = el.querySelector( '.js-moreToggleText' );
		this.activeTagsEl = el.querySelector( '.js-activeTags' );
		this.noResultsEl = document.querySelector( '.js-noResults' );

		this.groupEls   = [ ...el.querySelectorAll( '.js-filterGroup' ) ];
		this.modelCards = [ ...document.querySelectorAll( '.model-card' ) ];

		// groupId (string) → Set<number>
		this.selected    = new Map();
		this.panelOpen   = true;
		this.showingMore = false;
		this.isStuck     = false;
		// Tracks whether the user manually closed the panel. Used so that
		// auto-collapse on scroll-past can be safely auto-reopened on
		// scroll-back, without overriding a deliberate user choice.
		this.userClosed  = false;
		this.autoClosed  = false;

		// Availability filter state
		this.availEl          = el.querySelector( '.js-availabilityFilter' );
		this.availDateEl      = el.querySelector( '.js-availDate' );
		this.availDateWrapEl  = el.querySelector( '.p-modelFilters__availDateWrap' );
		this.availDateTextEl  = el.querySelector( '.js-availDateText' );
		this.availSlotsEl     = el.querySelector( '.js-dagdelen' );
		this.availableIds = null; // null = no filter active; Set<number> = filter active
		this.slotsByModelId = new Map(); // Map<number, string[]>
		this.activeSlot   = null;
		this.availTimer   = null;
		this.slotLabels   = this.collectSlotLabels();

		this.initSticky();
		this.bindBar();
		this.bindGroups();
		this.bindCheckboxes();
		this.bindAvailability();
		this.bindReset();
		this.bindMoreToggle();
		this.onFilterChange();
		this.updateSlotDisabledState();
	}

	// ── Sticky detection ───────────────────────────────────────────────────────
	// A 1px sentinel in normal flow is observed via IntersectionObserver.
	// When it exits the viewport (offset by navbar height), is-stuck is added
	// and the panel is auto-collapsed.
	//
	// To avoid the model grid below "snapping" upwards when the panel goes
	// out of flow (position: absolute), we first lock the wrapper's height
	// at its current full height via min-height, then animate min-height
	// down to bar height. The CSS transition on min-height makes the grid
	// slide up smoothly instead of jumping in one frame.
	//
	// To prevent jitter at the boundary, we apply hysteresis: collapse only
	// fires after the user has scrolled STICK_BUFFER pixels past the wrapper's
	// sticking point, and re-expand only fires once they scroll back up to
	// the natural sticking point. We get this by re-observing the sentinel
	// with two different rootMargin values depending on stuck state.
	initSticky() {
		const nav  = document.querySelector( '.tm-navbar' );
		const navH = nav ? Math.round( nav.getBoundingClientRect().height ) : 60;

		this.navH        = navH;
		this.stickBuffer = 60;

		const sentinel = document.createElement( 'div' );
		sentinel.setAttribute( 'aria-hidden', 'true' );
		sentinel.style.cssText = 'height:1px;pointer-events:none;';
		this.el.parentNode.insertBefore( sentinel, this.el );
		this.stickySentinel = sentinel;

		this.observeSticky();
	}

	// Uses a single stable observer (rootMargin = navH). Hysteresis on the way
	// back up is handled by checking boundingClientRect.top: we don't unstick
	// until the sentinel has re-entered at least stickBuffer px past the
	// sticky threshold. This avoids the oscillation caused by switching
	// rootMargin values inside the callback, which could immediately re-fire
	// the observer against the freshly-changed layout.
	observeSticky() {
		if ( this.stickyObserver ) {
			this.stickyObserver.disconnect();
		}

		this.stickyObserver = new IntersectionObserver(
			( [ entry ] ) => {
				const stuck = ! entry.isIntersecting;
				if ( stuck === this.isStuck ) return;

				// Hysteresis on scroll-up: only unstick once the sentinel has
				// cleared the sticky threshold by at least stickBuffer px.
				if ( ! stuck && entry.boundingClientRect.top < this.navH + this.stickBuffer ) {
					return;
				}

				this.isStuck = stuck;
				if ( stuck ) {
					this.enterStuck();
				} else {
					this.leaveStuck();
				}
			},
			{ rootMargin: `-${ this.navH }px 0px 0px 0px`, threshold: 0 }
		);
		this.stickyObserver.observe( this.stickySentinel );
	}

	enterStuck() {
		// Lock wrapper at its current full height so the grid below stays put
		// while the panel switches to position: absolute.
		const fullHeight = this.el.offsetHeight;
		this.el.style.minHeight = fullHeight + 'px';
		this.el.classList.add( 'is-stuck' );

		// Auto-collapse the panel (only if the user hadn't manually closed it).
		if ( this.panelOpen ) {
			this.autoClosed = true;
			this.setPanelOpen( false );
		}

		// In the next frame, animate min-height down to just the bar's height.
		// The CSS transition on min-height makes the grid slide up smoothly.
		requestAnimationFrame( () => {
			if ( ! this.isStuck ) return;
			const barHeight = this.barEl.offsetHeight;
			this.el.style.minHeight = barHeight + 'px';
		} );
	}

	leaveStuck() {
		this.el.classList.remove( 'is-stuck' );
		this.el.style.minHeight = '';

		// Restore the panel if it was auto-collapsed and the user hadn't
		// manually closed it before scrolling.
		if ( this.autoClosed && ! this.userClosed ) {
			this.setPanelOpen( true );
		}
		this.autoClosed = false;
	}

	// ── Panel open / close ────────────────────────────────────────────────────
	setPanelOpen( open ) {
		this.panelOpen = open;
		if ( open ) {
			this.panelEl.removeAttribute( 'hidden' );
			this.panelEl.setAttribute( 'aria-hidden', 'false' );
			this.toggleBtn.setAttribute( 'aria-expanded', 'true' );
			this.el.classList.remove( 'is-panelClosed' );
		} else {
			this.panelEl.setAttribute( 'hidden', '' );
			this.panelEl.setAttribute( 'aria-hidden', 'true' );
			this.toggleBtn.setAttribute( 'aria-expanded', 'false' );
			this.el.classList.add( 'is-panelClosed' );
			this.closeAllDropdowns();
		}
	}

	togglePanel() {
		// Manual click overrides any auto-collapse state.
		this.autoClosed = false;
		this.userClosed = this.panelOpen; // closing now → user closed
		this.setPanelOpen( ! this.panelOpen );
	}

	// ── Bar click ─────────────────────────────────────────────────────────────
	bindBar() {
		this.barEl.addEventListener( 'click', () => this.togglePanel() );
	}

	// ── Group dropdowns ───────────────────────────────────────────────────────
	bindGroups() {
		this.groupEls.forEach( group => {
			const trigger  = group.querySelector( '.js-groupTrigger' );
			const dropdown = group.querySelector( '.js-groupDropdown' );
			if ( ! trigger || ! dropdown ) return;

			trigger.addEventListener( 'click', e => {
				e.stopPropagation();
				const isOpen = group.classList.contains( 'is-open' );
				this.closeAllDropdowns();
				if ( ! isOpen ) {
					trigger.setAttribute( 'aria-expanded', 'true' );
					dropdown.removeAttribute( 'hidden' );
					group.classList.add( 'is-open' );
				}
			} );
		} );

		// Close dropdowns when clicking outside the filter block
		document.addEventListener( 'click', e => {
			if ( ! this.el.contains( e.target ) ) {
				this.closeAllDropdowns();
			}
		} );
	}

	closeAllDropdowns() {
		this.groupEls.forEach( group => {
			const trigger  = group.querySelector( '.js-groupTrigger' );
			const dropdown = group.querySelector( '.js-groupDropdown' );
			if ( trigger )  trigger.setAttribute( 'aria-expanded', 'false' );
			if ( dropdown ) dropdown.setAttribute( 'hidden', '' );
			group.classList.remove( 'is-open' );
		} );
	}

	// ── Checkboxes ────────────────────────────────────────────────────────────
	bindCheckboxes() {
		this.el.querySelectorAll( 'input[type="checkbox"]' ).forEach( cb => {
			cb.addEventListener( 'change', () => {
				const groupId = cb.dataset.groupId;
				const termId  = Number( cb.value );
				if ( ! this.selected.has( groupId ) ) this.selected.set( groupId, new Set() );
				const set = this.selected.get( groupId );
				cb.checked ? set.add( termId ) : set.delete( termId );
				this.onFilterChange();
			} );
		} );
	}

	// ── Availability filter ───────────────────────────────────────────────────
	collectSlotLabels() {
		const map = {};
		if ( ! this.availSlotsEl ) return map;
		this.availSlotsEl.querySelectorAll( '[data-slot]' ).forEach( btn => {
			const code  = btn.dataset.slot;
			const small = btn.querySelector( 'small' );
			map[ code ] = small ? small.textContent.trim() : code;
		} );
		return map;
	}

	updateDateLabel( value ) {
		if ( ! this.availDateTextEl || ! this.availDateWrapEl ) return;
		if ( value ) {
			const d = new Date( value + 'T00:00:00' );
			const formatted = d.toLocaleDateString( 'nl-NL', { day: 'numeric', month: 'short' } );
			this.availDateTextEl.textContent = formatted;
			this.availDateWrapEl.classList.add( 'has-date' );
		} else {
			this.availDateTextEl.textContent = 'Kies datum';
			this.availDateWrapEl.classList.remove( 'has-date' );
		}
		this.updateSlotDisabledState();
	}

	updateSlotDisabledState() {
		if ( ! this.availSlotsEl ) return;
		const hasDate = this.availDateEl && !! this.availDateEl.value;
		this.availSlotsEl.querySelectorAll( '[data-slot]' ).forEach( b => {
			b.disabled = ! hasDate;
		} );
	}

	resetAvailFilter() {
		if ( this.availDateEl ) this.availDateEl.value = '';
		this.updateDateLabel( '' );
		this.availableIds   = null;
		this.slotsByModelId = new Map();
		this.activeSlot     = null;
		if ( this.availSlotsEl ) {
			this.availSlotsEl.querySelectorAll( '[data-slot]' ).forEach( b => {
				b.classList.remove( 'is-active' );
				b.setAttribute( 'aria-pressed', 'false' );
			} );
		}
		this.clearCardSlots();
	}

	bindAvailability() {
		if ( this.availDateWrapEl && this.availDateEl ) {
			this.availDateWrapEl.addEventListener( 'click', () => {
				try {
					this.availDateEl.showPicker();
				} catch {
					this.availDateEl.focus();
				}
			} );
		}

		if ( this.availDateEl ) {
			this.availDateEl.addEventListener( 'change', () => {
				this.updateDateLabel( this.availDateEl.value );
				this.onAvailChange();
			} );
		}

		if ( this.availSlotsEl ) {
			this.availSlotsEl.addEventListener( 'click', e => {
				const btn = e.target.closest( '[data-slot]' );
				if ( ! btn ) return;
				const slot = btn.dataset.slot;
				if ( this.activeSlot === slot ) {
					this.activeSlot = null;
					btn.classList.remove( 'is-active' );
					btn.setAttribute( 'aria-pressed', 'false' );
				} else {
					this.availSlotsEl.querySelectorAll( '[data-slot]' ).forEach( b => {
						b.classList.remove( 'is-active' );
						b.setAttribute( 'aria-pressed', 'false' );
					} );
					this.activeSlot = slot;
					btn.classList.add( 'is-active' );
					btn.setAttribute( 'aria-pressed', 'true' );
				}
				this.onAvailChange();
			} );
		}
	}

	onAvailChange() {
		clearTimeout( this.availTimer );
		const date = this.availDateEl ? this.availDateEl.value : '';
		if ( ! date ) {
			this.availableIds   = null;
			this.slotsByModelId = new Map();
			this.clearCardSlots();
			this.onFilterChange();
			return;
		}
		this.availTimer = setTimeout( () => this.fetchAvailability( date, this.activeSlot ), 300 );
	}

	fetchAvailability( date, slot ) {
		const ajaxUrl = this.availEl ? this.availEl.dataset.ajaxUrl : '';
		if ( ! ajaxUrl ) return;

		this.setAvailLoading( true );

		const data = new FormData();
		data.append( 'action', 'sb_check_model_availability' );
		data.append( 'date', date );
		if ( slot ) data.append( 'slot', slot );

		fetch( ajaxUrl, { method: 'POST', body: data } )
			.then( r => r.json() )
			.then( result => {
				if ( result.success ) {
					this.availableIds   = new Set( ( result.data.ids || [] ).map( Number ) );
					this.slotsByModelId = this.buildSlotsMap( result.data.slotsById );
				} else {
					this.availableIds   = null;
					this.slotsByModelId = new Map();
				}
				this.onFilterChange();
			} )
			.catch( () => {
				this.availableIds   = null;
				this.slotsByModelId = new Map();
				this.onFilterChange();
			} )
			.finally( () => this.setAvailLoading( false ) );
	}

	buildSlotsMap( slotsById ) {
		const map = new Map();
		if ( ! slotsById || typeof slotsById !== 'object' ) return map;
		Object.keys( slotsById ).forEach( key => {
			const id    = Number( key );
			const slots = Array.isArray( slotsById[ key ] ) ? slotsById[ key ] : [];
			if ( id ) map.set( id, slots );
		} );
		return map;
	}

	renderCardSlots() {
		// Only render when a date filter is active.
		const dateActive = !! ( this.availDateEl && this.availDateEl.value );
		if ( ! dateActive ) {
			this.clearCardSlots();
			return;
		}

		this.modelCards.forEach( card => {
			const target = card.querySelector( '.js-modelCardDagdelen' );
			if ( ! target ) return;

			const id    = Number( card.dataset.postId );
			const slots = this.slotsByModelId.get( id ) || [];
			if ( slots.length === 0 ) {
				target.textContent = '';
				target.setAttribute( 'hidden', '' );
				return;
			}

			const labels = slots.map( s => this.slotLabels[ s ] || s );
			const prefix = target.dataset.prefix || '';
			target.textContent = prefix ? `${ prefix } ${ labels.join( ', ' ) }` : labels.join( ', ' );
			target.removeAttribute( 'hidden' );
		} );
	}

	clearCardSlots() {
		this.modelCards.forEach( card => {
			const target = card.querySelector( '.js-modelCardDagdelen' );
			if ( ! target ) return;
			target.textContent = '';
			target.setAttribute( 'hidden', '' );
		} );
	}

	setAvailLoading( loading ) {
		if ( this.availEl ) {
			this.availEl.toggleAttribute( 'data-loading', loading );
		}
	}

	// ── Reset ─────────────────────────────────────────────────────────────────
	bindReset() {
		if ( ! this.resetBtn ) return;
		this.resetBtn.addEventListener( 'click', () => {
			this.selected.clear();
			this.el.querySelectorAll( 'input[type="checkbox"]' ).forEach( cb => {
				cb.checked = false;
			} );
			this.resetAvailFilter();
			this.onFilterChange();
		} );
	}

	// ── Toon meer / minder ────────────────────────────────────────────────────
	bindMoreToggle() {
		if ( ! this.moreToggle ) return;
		this.moreToggle.addEventListener( 'click', e => {
			e.stopPropagation();
			this.showingMore = ! this.showingMore;
			this.el.classList.toggle( 'is-showingMore', this.showingMore );
			if ( this.moreText ) {
				this.moreText.textContent = this.showingMore ? 'Toon minder' : 'Toon meer';
			}
		} );
	}

	// ── Filter change ─────────────────────────────────────────────────────────
	onFilterChange() {
		this.filterModels();
		this.renderCardSlots();
		this.updateBadge();
		this.updateGroupCounts();
		this.updateActiveTags();
	}

	filterModels() {
		// Taxonomy: AND across groups, OR within group.
		const activeGroups = [ ...this.selected.entries() ].filter( ( [ , s ] ) => s.size > 0 );
		let visibleCount   = 0;

		this.modelCards.forEach( card => {
			const rawTerms  = card.dataset.filterTerms || '';
			const cardTerms = new Set( rawTerms.split( ',' ).map( Number ).filter( Boolean ) );

			const taxMatch = activeGroups.length === 0 ||
				activeGroups.every( ( [ , termSet ] ) => [ ...termSet ].some( id => cardTerms.has( id ) ) );

			// Availability: null = no filter; Set<number> = must be in set.
			const availMatch = this.availableIds === null ||
				this.availableIds.has( Number( card.dataset.postId ) );

			const visible = taxMatch && availMatch;
			card.style.display = visible ? '' : 'none';
			if ( visible ) visibleCount++;
		} );

		if ( this.noResultsEl ) {
			this.noResultsEl.hidden = visibleCount > 0;
		}
	}

	updateBadge() {
		let count = 0;
		this.selected.forEach( s => { count += s.size; } );
		if ( this.availDateEl && this.availDateEl.value ) count++;
		if ( count > 0 ) {
			this.el.classList.add( 'has-filters' );
		} else {
			this.el.classList.remove( 'has-filters' );
		}
		if ( this.badgeEl ) {
			if ( count > 0 ) {
				this.badgeEl.textContent = count;
				this.badgeEl.removeAttribute( 'hidden' );
			} else {
				this.badgeEl.setAttribute( 'hidden', '' );
			}
		}
		if ( this.resetBtn ) {
			if ( count > 0 ) {
				this.resetBtn.removeAttribute( 'aria-disabled' );
			} else {
				this.resetBtn.setAttribute( 'aria-disabled', 'true' );
			}
		}
	}

	updateGroupCounts() {
		this.groupEls.forEach( group => {
			const groupId = group.dataset.groupId;
			const countEl = group.querySelector( '.js-groupCount' );
			if ( ! countEl ) return;
			let count;
			if ( this.availSlotsEl && group.contains( this.availSlotsEl ) ) {
				count = this.activeSlot ? 1 : 0;
			} else {
				const set = this.selected.get( groupId );
				count = set ? set.size : 0;
			}
			if ( count > 0 ) {
				countEl.textContent = count;
				countEl.removeAttribute( 'hidden' );
				group.classList.add( 'has-selection' );
			} else {
				countEl.setAttribute( 'hidden', '' );
				group.classList.remove( 'has-selection' );
			}
		} );
	}

	updateActiveTags() {
		if ( ! this.activeTagsEl ) return;
		this.activeTagsEl.innerHTML = '';
		let hasAny = false;

		// Date tag
		if ( this.availDateEl && this.availDateEl.value ) {
			hasAny = true;
			const d = new Date( this.availDateEl.value + 'T00:00:00' );
			const formatted = d.toLocaleDateString( 'nl-NL', { day: 'numeric', month: 'short' } );
			const tag = document.createElement( 'button' );
			tag.type      = 'button';
			tag.className = 'p-modelFilters__tag';
			tag.setAttribute( 'aria-label', `Verwijder datumfilter: ${ formatted }` );
			tag.innerHTML = `<span>${ formatted }</span><span class="p-modelFilters__tagRemove" aria-hidden="true">&times;</span>`;
			tag.addEventListener( 'click', () => {
				this.resetAvailFilter();
				this.onFilterChange();
			} );
			this.activeTagsEl.appendChild( tag );
		}

		// Dagdeel tag
		if ( this.activeSlot ) {
			hasAny = true;
			const label = this.slotLabels[ this.activeSlot ] || this.activeSlot;
			const tag = document.createElement( 'button' );
			tag.type      = 'button';
			tag.className = 'p-modelFilters__tag';
			tag.setAttribute( 'aria-label', `Verwijder filter: ${ label }` );
			tag.innerHTML = `<span>${ label }</span><span class="p-modelFilters__tagRemove" aria-hidden="true">&times;</span>`;
			tag.addEventListener( 'click', () => {
				const btn = this.availSlotsEl && this.availSlotsEl.querySelector( `[data-slot="${ this.activeSlot }"]` );
				if ( btn ) {
					btn.classList.remove( 'is-active' );
					btn.setAttribute( 'aria-pressed', 'false' );
				}
				this.activeSlot = null;
				this.onAvailChange();
			} );
			this.activeTagsEl.appendChild( tag );
		}

		this.selected.forEach( ( termSet, groupId ) => {
			termSet.forEach( termId => {
				const cb = this.el.querySelector( `input[value="${ termId }"]` );
				if ( ! cb ) return;
				hasAny = true;

				const tag = document.createElement( 'button' );
				tag.type      = 'button';
				tag.className = 'p-modelFilters__tag';
				tag.setAttribute( 'aria-label', `Verwijder filter: ${ cb.dataset.label || termId }` );
				tag.innerHTML = `<span>${ cb.dataset.label || termId }</span><span class="p-modelFilters__tagRemove" aria-hidden="true">&times;</span>`;
				tag.addEventListener( 'click', () => {
					cb.checked = false;
					const s = this.selected.get( groupId );
					if ( s ) s.delete( termId );
					this.onFilterChange();
				} );

				this.activeTagsEl.appendChild( tag );
			} );
		} );

		this.activeTagsEl.classList.toggle( 'is-visible', hasAny );
	}
}

document.querySelectorAll( '.js-modelFilters' ).forEach( el => new ModelFilters( el ) );
